import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { ORDER_ALERT_PREFERENCES_STORAGE_KEY } from 'src/app/shared/preserved-storage-keys';
import { ORDER_LINE_ALERT_TYPES } from '../models/order-alert-types';

/**
 * What is switched OFF, not what is switched on. An alert type the backend adds
 * later has to be on everywhere, including on a browser that already saved a
 * choice — an allow-list would silently hide it.
 */
interface IOrderAlertPreferences {
  disabled: string[];
}

/**
 * Which order-line alerts this browser shows, as chosen in Settings → Orders → Alerts.
 *
 * Client-side only: the alerts are computed by the backend and this ticket adds no
 * endpoint to hold a choice, so the preference does not follow the user to another
 * browser or device. It does survive a logout — the key is registered in
 * `PRESERVED_STORAGE_KEYS`, so an involuntary 401 cannot silently reset it.
 */
@Injectable({ providedIn: 'root' })
export class OrderAlertPreferencesService {
  private disabled: string[] = this.read().disabled;

  /**
   * What is switched off, emitting on every change. Read it as-is: the list is
   * open-ended, so a type it does not carry is one the user never touched.
   */
  readonly disabledTypes$ = new BehaviorSubject<string[]>([...this.disabled]);

  get disabledTypes(): string[] {
    return this.disabledTypes$.value;
  }

  /**
   * A type the screen does not offer is never disabled: nothing can have switched
   * it off, so nothing may quietly hide it.
   */
  isEnabled(type: string): boolean {
    return !this.disabled.includes(type);
  }

  setEnabled(type: string, enabled: boolean): void {
    if (!ORDER_LINE_ALERT_TYPES.some((alert) => alert.type === type)) return;
    this.disabled = enabled
      ? this.disabled.filter((disabled) => disabled !== type)
      : [...this.disabled, type];
    this.write({ disabled: this.disabled });
    this.disabledTypes$.next([...this.disabled]);
  }

  private read(): IOrderAlertPreferences {
    try {
      const raw = localStorage.getItem(ORDER_ALERT_PREFERENCES_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && Array.isArray(parsed.disabled)) {
        return { disabled: parsed.disabled.filter((type: unknown) => typeof type === 'string') };
      }
    } catch {
      // fall through to the default on malformed JSON / disabled storage
    }
    return { disabled: [] };
  }

  private write(preferences: IOrderAlertPreferences): void {
    try {
      localStorage.setItem(ORDER_ALERT_PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
    } catch {
      // ignore quota / disabled storage errors — the choice still applies for this session
    }
  }
}
